<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Please provide information about your joint account arrangement.
          This information is required for proper account setup and compliance.
        </p>
      </div>
    </help-drawer>
    <v-form name="PershingJointAccountInfo" class="form" data-test-id="pershing-joint-account-info-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id" :instance="instance"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="pershing-joint-account-info-header">
              <span class="headerlight">
                Provide <strong>Joint Account Information</strong> for
                <strong>{{ label }}</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving joint account information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isPershingJointAccountInfoComplete && formHidden && !isSyncing"
          :submitted="isPershingJointAccountInfoComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-row class="justify-center" v-if="!formHidden && !isSyncing">
          <v-col class="step-form-container large">
            <v-row>
              <v-col cols="12" md="6">
                <!-- Joint Tenancy State -->
                <label for="jointTenancyState" class="required" data-test-id="joint-tenancy-state-label">
                  Joint Tenancy State
                </label>
                <v-select
                  v-model="jointAccountInfo.jointTenancyState"
                  :items="states"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Choose State"
                  @change="$v.jointAccountInfo.jointTenancyState.$touch()"
                  :error-messages="jointTenancyStateErrors"
                  data-test-id="joint-tenancy-state-select"
                  class="mb-4"
                />
              </v-col>
              <v-col cols="12" md="6">
                <!-- Joint Married to each other -->
                <label class="required mb-2 d-block" data-test-id="joint-married-label">
                  Joint Married to each other
                </label>
                <v-radio-group
                  v-model="jointAccountInfo.jointMarriedToEachOther"
                  :error-messages="jointMarriedToEachOtherErrors"
                  hide-details="auto"
                  row
                  @change="$v.jointAccountInfo.jointMarriedToEachOther.$touch()"
                  data-test-id="joint-married-radio-group"
                  class="mt-1"
                >
                  <v-radio
                    :ripple="false"
                    label="Yes"
                    :value="true"
                    data-test-id="joint-married-yes"
                  ></v-radio>
                  <v-radio
                    :ripple="false"
                    label="No"
                    :value="false"
                    data-test-id="joint-married-no"
                    class="ml-4"
                  ></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="pershing-joint-account-info-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS, STATES } from '@/common/constants';
import { required } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'PershingJointAccountInfo',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],
  
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    jointAccountInfo: {
      jointTenancyState: null,
      jointMarriedToEachOther: null,
    },
    MENUS: MENUS,
    states: STATES,
  }),
  
  validations() {
    return {
      jointAccountInfo: {
        jointTenancyState: { required },
        jointMarriedToEachOther: { required },
      },
      validationGroup: ['jointAccountInfo'],
    };
  },
  
  computed: {
    instance() {
      return {
        name: this.name,
        label: this.label,
      };
    },
    
    isPershingJointAccountInfoComplete() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id, this.instance);
      return page && page.lastCompleted;
    },
    
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    
    helpTitle() {
      return 'Joint Account Information';
    },
    
    // Error messages
    jointTenancyStateErrors() {
      const errors = [];
      if (!this.$v.jointAccountInfo.jointTenancyState.$dirty) return errors;
      !this.$v.jointAccountInfo.jointTenancyState.required && errors.push('Joint tenancy state is required');
      return errors;
    },
    
    jointMarriedToEachOtherErrors() {
      const errors = [];
      if (!this.$v.jointAccountInfo.jointMarriedToEachOther.$dirty) return errors;
      !this.$v.jointAccountInfo.jointMarriedToEachOther.required && errors.push('This field is required');
      return errors;
    },
  },
  
  methods: {
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];
      
      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }
      
      const data = {
        instance: this.instance,
        jointAccountInfo: this.jointAccountInfo,
      };
      
      this.completeStep(MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id, data, this.instance)
        .then(() => {
          this.goNext(to);
        })
        .catch(() => {});
    },
    
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },
    
    skip() {
      this.goNext(this.pages[this.pageIndex + 1]);
    },
    
    leave() {
      const route = this.leaveRoute;
      
      const data = {
        instance: this.instance,
        jointAccountInfo: this.jointAccountInfo,
      };
      
      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id, this.instance);
      
      this.completeStep(MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id, data, this.instance)
        .then(() => {
          this.goNext(route);
        })
        .catch(() => {});
    },
  },
  
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex;
    let i = 0;
    for (const page of this.pages) {
      if (typeof page === 'object' && window.location.pathname.includes(page.pathname.replaceAll(' ', '%20'))) {
        this.pageIndex = i;
      }
      i++;
    }
    
    this.formHidden = this.isPershingJointAccountInfoComplete;
  },
};
</script>

<style lang="scss" scoped>
.syncing-message {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

.step-form-container {
  &.large {
    max-width: 800px;
  }
}

.help-link {
  padding-top: 8px;
  
  a {
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      text-decoration: none;
    }
  }
}

.skip-for-now {
  margin-top: 20px;
  text-align: center;
  
  a {
    color: #666;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      text-decoration: none;
    }
  }
}
</style>
